import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LabelPrintingApiError,
  labelPrintingApi,
  type IsbtPrinterPayload,
  type IsbtTemplatePayload,
  type PrintContextPayload,
  type PrintLabelsPayload,
  type TestPrintPayload,
  type ZebraApprovePayload,
  type ZebraDraftPayload,
  type ZebraPrinterPayload,
  type ZebraTemplateCreatePayload,
} from '@/api/labelPrintingApi'
import type { LabelTypeSetting } from '@/api/labelPrintingApi'
import type { LabelType, PrinterFamily } from '@/lib/labelPrinting'
import { useTenant } from '@/providers/TenantProvider'

/** Prefix key for every label printing query, for invalidation. */
const LABEL_PRINTING_KEY = ['label-printing'] as const

/**
 * One query per list (z8nrz7evkc). Each list is small and fetched whole. The
 * tenant is in the key so a switch can never paint another tenant's rows.
 */
function useLabelPrintingQuery<T>(kind: string, queryFn: () => Promise<T>) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...LABEL_PRINTING_KEY, kind, selectedTenantId],
    enabled: !!selectedTenantId,
    queryFn,
  })
}

export const useLabelPrintingOptions = () => useLabelPrintingQuery('options', labelPrintingApi.options)
export const useIsbtTemplates = () => useLabelPrintingQuery('isbt-templates', labelPrintingApi.listIsbtTemplates)
export const useIsbtPrinters = () => useLabelPrintingQuery('isbt-printers', labelPrintingApi.listIsbtPrinters)
export const useZebraTemplates = () => useLabelPrintingQuery('zebra-templates', labelPrintingApi.listZebraTemplates)
export const useZebraPrinters = () => useLabelPrintingQuery('zebra-printers', labelPrintingApi.listZebraPrinters)
export const useLabelTypes = () => useLabelPrintingQuery('label-types', labelPrintingApi.listLabelTypes)

/**
 * Every write refreshes every list and the options: a retired template
 * changes how printers list it, and a new location becomes a suggestion.
 */
export function useLabelPrintingMutations() {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: LABEL_PRINTING_KEY })
  }
  const createIsbtTemplate = useMutation({
    mutationFn: (payload: IsbtTemplatePayload) => labelPrintingApi.createIsbtTemplate(payload),
    onSuccess: invalidate,
  })
  const updateIsbtTemplate = useMutation({
    mutationFn: (input: { id: string; payload: Partial<IsbtTemplatePayload> & { active?: boolean } }) =>
      labelPrintingApi.updateIsbtTemplate(input.id, input.payload),
    onSuccess: invalidate,
  })
  const approveIsbtTemplate = useMutation({
    mutationFn: (id: string) => labelPrintingApi.approveIsbtTemplate(id),
    onSuccess: invalidate,
  })
  const createIsbtPrinter = useMutation({
    mutationFn: (payload: IsbtPrinterPayload) => labelPrintingApi.createIsbtPrinter(payload),
    onSuccess: invalidate,
  })
  const updateIsbtPrinter = useMutation({
    mutationFn: (input: { id: string; payload: Partial<IsbtPrinterPayload> & { active?: boolean } }) =>
      labelPrintingApi.updateIsbtPrinter(input.id, input.payload),
    onSuccess: invalidate,
  })
  // Writes a history entry server-side but changes no list row.
  const testPrint = useMutation({
    mutationFn: (payload: TestPrintPayload) => labelPrintingApi.testPrint(payload),
  })
  // A 409 on a Zebra template means someone else changed it first: refresh,
  // so the screen shows what the server now holds.
  const invalidateOnConflict = (err: unknown) => {
    if (err instanceof LabelPrintingApiError && err.status === 409) invalidate()
  }
  const createZebraTemplate = useMutation({
    mutationFn: (payload: ZebraTemplateCreatePayload) => labelPrintingApi.createZebraTemplate(payload),
    onSuccess: invalidate,
    onError: invalidateOnConflict,
  })
  const saveZebraDraft = useMutation({
    mutationFn: (input: { id: string; payload: ZebraDraftPayload }) =>
      labelPrintingApi.saveZebraDraft(input.id, input.payload),
    onSuccess: invalidate,
    onError: invalidateOnConflict,
  })
  const approveZebraTemplate = useMutation({
    mutationFn: (input: { id: string; payload: ZebraApprovePayload }) =>
      labelPrintingApi.approveZebraTemplate(input.id, input.payload),
    onSuccess: invalidate,
    onError: invalidateOnConflict,
  })
  const updateZebraTemplate = useMutation({
    mutationFn: (input: { id: string; payload: { name?: string; active?: boolean } }) =>
      labelPrintingApi.updateZebraTemplate(input.id, input.payload),
    onSuccess: invalidate,
    onError: invalidateOnConflict,
  })
  const createZebraPrinter = useMutation({
    mutationFn: (payload: ZebraPrinterPayload) => labelPrintingApi.createZebraPrinter(payload),
    onSuccess: invalidate,
  })
  const updateZebraPrinter = useMutation({
    mutationFn: (input: { id: string; payload: Partial<ZebraPrinterPayload> & { active?: boolean } }) =>
      labelPrintingApi.updateZebraPrinter(input.id, input.payload),
    onSuccess: invalidate,
  })
  // The saved row goes into the cache first, so the table shows the new
  // printer type even if the refresh that follows fails; the mutation then
  // resolves once the lists are fetched again. A printer write above also
  // refreshes this table: it changes the active printer counts.
  const updateLabelType = useMutation({
    mutationFn: (input: { labelType: LabelType; printerFamily: PrinterFamily }) =>
      labelPrintingApi.updateLabelType(input.labelType, input.printerFamily),
    onSuccess: saved => {
      qc.setQueriesData<LabelTypeSetting[]>({ queryKey: [...LABEL_PRINTING_KEY, 'label-types'] }, rows =>
        rows?.map(r => (r.labelType === saved.labelType ? saved : r)),
      )
      return qc.invalidateQueries({ queryKey: LABEL_PRINTING_KEY })
    },
  })
  return {
    createIsbtTemplate,
    updateIsbtTemplate,
    approveIsbtTemplate,
    createIsbtPrinter,
    updateIsbtPrinter,
    testPrint,
    createZebraTemplate,
    saveZebraDraft,
    approveZebraTemplate,
    updateZebraTemplate,
    createZebraPrinter,
    updateZebraPrinter,
    updateLabelType,
  }
}

/**
 * What the print popup shows for these labels: the printers of the family
 * Printer association names, their templates, and each label's values. Off
 * until the popup is open (`payload` null). Always fetched fresh: a printer
 * may have been retired since the popup last opened.
 */
export function usePrintContext(payload: PrintContextPayload | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...LABEL_PRINTING_KEY, 'print-context', selectedTenantId, payload],
    enabled: !!selectedTenantId && payload !== null && payload.ids.length > 0,
    queryFn: () => labelPrintingApi.printContext(payload!),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

/** Send the labels to a printer. One job per label, all in one request. */
export function usePrintLabels() {
  return useMutation({
    mutationFn: (payload: PrintLabelsPayload) => labelPrintingApi.print(payload),
  })
}
